"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PFFKit = exports.MatchupType = void 0;
const pffkit_js_core_1 = require("@pro-football-focus/pffkit-js-core");
const wr_cb_request_1 = require("./requests/wr_cb_request");
const player_team_matchup_request_1 = require("./requests/player_team_matchup_request");
const historical_performances_request_1 = require("./requests/historical_performances_request");
const te_matchups_request_1 = require("./requests/te_matchups_request");
const qb_matchups_request_1 = require("./requests/qb_matchups_request");
const rb_matchups_request_1 = require("./requests/rb_matchups_request");
const events_request_1 = require("./requests/events_request");
const teams_request_1 = require("./requests/teams_request");
const mismatches_request_1 = require("./requests/mismatches_request");
const hot_players_request_1 = require("./requests/hot_players_request");
const similar_receivers_request_1 = require("./requests/similar_receivers_request");
const prop_bet_request_1 = require("./requests/prop_bet_request");
function errorMessage(error) {
    return error.message ?? error.error ?? 'Unknown error';
}
/**
 * The type of matchup to retrieve.
 */
var MatchupType;
(function (MatchupType) {
    MatchupType[MatchupType["Team"] = 0] = "Team";
    MatchupType[MatchupType["QuarterBack"] = 1] = "QuarterBack";
    MatchupType[MatchupType["TightEnd"] = 2] = "TightEnd";
    MatchupType[MatchupType["RunningBackRushing"] = 3] = "RunningBackRushing";
    MatchupType[MatchupType["RunningBackReceiving"] = 4] = "RunningBackReceiving";
    MatchupType[MatchupType["WideReceiver"] = 5] = "WideReceiver";
})(MatchupType || (exports.MatchupType = MatchupType = {}));
/**
 * Primary class for interacting with the PFFKit native module
 */
class PFFKit {
    /**
     * Create a new instance of the PFFKit client.
     *
     * @param environment - The environment to use for the client.
     */
    constructor(environment) {
        this.environment = environment;
        this.teamsCache = new pffkit_js_core_1.TeamsCache();
        /**
         * The current season that the client is using.
         *
         * Defaults to Season.twentyTwentyThree
         */
        this.currentSeason = pffkit_js_core_1.Season.twentyTwentyThree;
        this.environment = environment;
    }
    /** {@inheritdoc ApiClient.setAPIKey} */
    setAPIKey(key) {
        this.environment.apiKey = key;
    }
    //#region Events API
    /** {@inheritDoc ApiClient.getEventsForSeason} */
    async getEventsForSeason(season, sportsbook, includeScoringData, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        let path = `/seasons/${season}`;
        if (week) {
            path += `/weeks/${week}/events`;
        }
        else {
            path += '/events';
        }
        request.path = path;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            scoreData: includeScoringData ? 'true' : 'false',
        });
        try {
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            const data = await this.makeRequest(request);
            const events = data?.events.map((event) => {
                const awayTeam = teams.find((team) => team.id === event.awayTeamId);
                const homeTeam = teams.find((team) => team.id === event.homeTeamId);
                if (!awayTeam || !homeTeam) {
                    throw new Error(`No team found for event ${event.eventId}. Response: "${JSON.stringify(data)}"`);
                }
                return { ...(0, events_request_1.createEvent)(event, awayTeam, homeTeam) };
            });
            if (events) {
                return [...events];
            }
            else {
                return Promise.reject(new Error(`No events found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            console.error(`Error fetching events for season ${season}:`, error);
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getEventWithID} */
    async getEventWithID(eventId, sportsbook, includeScoringData) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}`;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            scoreData: includeScoringData ? 'true' : 'false',
        });
        try {
            const data = await this.makeRequest(request);
            const event = data?.event;
            if (event.season == undefined) {
                return Promise.reject(new Error(`No season found for event ${eventId}. Response: "${JSON.stringify(data)}"`));
            }
            const teams = await this.getTeamsForSeason(event.season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${event.season}`));
            }
            const awayTeam = teams.find((team) => team.id === event.awayTeamId);
            const homeTeam = teams.find((team) => team.id === event.homeTeamId);
            if (event && awayTeam && homeTeam) {
                return { ...(0, events_request_1.createEvent)(event, awayTeam, homeTeam) };
            }
            else {
                return Promise.reject(new Error(`No event with id ${eventId} found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            console.error(`Error fetching event ${eventId}:`, error);
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Teams API
    /** {@inheritdoc ApiClient.getTeamsForSeason} */
    async getTeamsForSeason(season, ignoreCache = false) {
        if ((this.teamsCache.isSeasonCached(season), !ignoreCache)) {
            return Promise.resolve(this.teamsCache.getTeamsForSeason(season));
        }
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/teams`;
        try {
            const data = await this.makeRequest(request);
            const teams = data?.teams.map((team) => ({ ...(0, teams_request_1.createTeam)(team) }));
            if (teams) {
                return [...teams];
            }
            else {
                return Promise.reject(new Error(`No teams for season ${season} found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            console.error(`Error fetching teams for season ${season}:`, error);
            return Promise.reject(error);
        }
    }
    /** {@inheritdoc ApiClient.getDepthChartForTeam} */
    async getDepthChartForTeam(teamId) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/teams/${teamId}/depth-chart`;
        try {
            const teams = await this.getTeamsForSeason(this.currentSeason);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${this.currentSeason}`));
            }
            const data = await this.makeRequest(request);
            const team = teams.find((team) => team.id === data.teamId);
            if (team == undefined) {
                return Promise.reject(new Error(`No team found for ${teamId}. Response: "${JSON.stringify(data)}"`));
            }
            const players = await this.getPlayerProfiles(data.players.map((player) => player.playerId));
            // Update depthOrder field for matching players
            players.forEach((player) => {
                const matchingPlayer = data.players.find((p) => p.playerId === player.id);
                if (matchingPlayer?.depthOrder && player.depthChartPosition) {
                    player.depthChartPosition.depthOrder = matchingPlayer.depthOrder;
                }
            });
            return Promise.resolve({
                team: team,
                players: players,
            });
        }
        catch (error) {
            console.error(`Error fetching depth chart for team ${teamId}:`, error);
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Player APIs
    /** {@inheritdoc ApiClient.getPlayerProfile} */
    async getPlayerProfile(playerId) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
            league: pffkit_js_core_1.League.None,
            path: `/players/${playerId}`,
        };
        try {
            const teams = await this.getTeamsForSeason(this.currentSeason);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${this.currentSeason}`));
            }
            const data = await this.makeRequest(request);
            const team = teams.find((team) => team.id === data.teamId);
            if (team == undefined) {
                return Promise.reject(new Error(`No team found for player ${playerId}. Response: "${JSON.stringify(data)}"`));
            }
            const depthChartPosition = data.depthChartPosition === null
                ? undefined
                : { position: data.depthChartPosition, depthOrder: 1 };
            const birthdate = data.birthdate ? new Date(data.birthdate) : undefined;
            const player = {
                firstName: data.firstName,
                lastName: data.lastName,
                id: data.playerId,
                position: data.position,
                team: team,
                depthChartPosition: depthChartPosition,
                height: data.height ?? undefined,
                weight: data.weight ?? undefined,
                jerseyNumber: data.jerseyNumber ?? undefined,
                birthdate: birthdate,
                grades: data.grades.map((grade) => {
                    return {
                        facet: grade.facet,
                        grade: grade.gradeValue,
                        rank: grade.gradeRank,
                        totalRank: grade.gradeRankTotal,
                    };
                }),
            };
            if (player) {
                return { ...player };
            }
            else {
                return Promise.reject(new Error(`No player with PFF ID ${playerId} found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            console.error(`Error fetching player with PFF ID ${playerId}:`, error);
            return Promise.reject(error);
        }
    }
    /** {@inheritdoc ApiClient.getPlayerProfiles} */
    async getPlayerProfiles(playerIds) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
            league: pffkit_js_core_1.League.None,
            path: '/players',
            queryParameters: new URLSearchParams({
                playerIds: playerIds.join(','),
            }),
        };
        try {
            const teams = await this.getTeamsForSeason(this.currentSeason);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${this.currentSeason}`));
            }
            const data = await this.makeRequest(request);
            const response = data?.players;
            const players = response.map((player) => {
                const team = teams.find((team) => team.id === player.teamId);
                if (team == undefined) {
                    throw new pffkit_js_core_1.PFFKitError(400, `No team found for player ${player.playerId}. Response: "${JSON.stringify(player)}"`);
                }
                const depthChartPosition = player.depthChartPosition === null
                    ? undefined
                    : { position: player.depthChartPosition, depthOrder: 1 };
                const birthdate = player.birthdate ? new Date(player.birthdate) : undefined;
                const result = {
                    id: player.playerId,
                    firstName: player.firstName,
                    lastName: player.lastName,
                    position: player.position,
                    team: team,
                    depthChartPosition: depthChartPosition,
                    height: player.height,
                    weight: player.weight,
                    jerseyNumber: player.jerseyNumber ?? undefined,
                    birthdate: birthdate,
                    grades: player.grades.map((grade) => {
                        return {
                            facet: grade.facet,
                            grade: grade.gradeValue,
                            rank: grade.gradeRank,
                            totalRank: grade.gradeRankTotal,
                        };
                    }),
                };
                return result;
            });
            if (players) {
                return [...players];
            }
            else {
                return Promise.reject(new Error(`No player with PFF ID `)); // ${playerId} found. Response: "${JSON.stringify(data)}"`))
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Prop Bet API
    /**
     * {@inheritDoc ApiClient.getPropBetsForEvent}
     */
    async getPropBetsForEvent(eventId, sportsbook) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/player-props/event`;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            eventId: eventId.toString(),
            detailed: 'true',
        });
        const response = await this.makeRequest(request);
        // These are converted to a set to ensure we are removing duplicates from the response
        const playerIds = [...new Set(response.analysis.map((analysis) => analysis.playerId))];
        const eventIds = [...new Set(response.analysis.map((analysis) => analysis.gameId))];
        const players = await this.getPlayerProfiles(playerIds);
        const events = await this.getEvents(eventIds, sportsbook, true);
        try {
            return Promise.resolve((0, prop_bet_request_1.mapToPropBets)(response.analysis, players, events));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getPropBetsForTeam} */
    async getPropBetsForTeam(teamId, season, week, sportsbook) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week.toString()}/teams/${teamId}/player-props`;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            detailed: 'true',
        });
        const response = await this.makeRequest(request);
        // These are converted to a set to ensure we are removing duplicates from the response
        const playerIds = [...new Set(response.analysis.map((analysis) => analysis.playerId))];
        const eventIds = [...new Set(response.analysis.map((analysis) => analysis.gameId))];
        const players = await this.getPlayerProfiles(playerIds);
        const events = await this.getEvents(eventIds, sportsbook, true);
        try {
            return Promise.resolve((0, prop_bet_request_1.mapToPropBets)(response.analysis, players, events));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getPropBetsForWeek} */
    async getPropBetsForWeek(season, week, sportsbook) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week.toString()}/player-props`;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            detailed: 'true',
        });
        const response = await this.makeRequest(request);
        // These are converted to a set to ensure we are removing duplicates from the response
        const playerIds = [...new Set(response.analysis.map((analysis) => analysis.playerId))];
        const eventIds = [...new Set(response.analysis.map((analysis) => analysis.gameId))];
        const players = await this.getPlayerProfiles(playerIds);
        const events = await this.getEvents(eventIds, sportsbook, true);
        try {
            return Promise.resolve((0, prop_bet_request_1.mapToPropBets)(response.analysis, players, events));
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Recent Defenses API
    /** {@inheritDoc ApiClient.getRecentDefensesForEvent} */
    async getRecentDefensesForEvent(eventId) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}/recent-defenses`;
        try {
            const teams = await this.getTeamsForSeason(pffkit_js_core_1.Season.twentyTwentyThree);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${pffkit_js_core_1.Season.twentyTwentyThree}`));
            }
            const responseItems = (await this.makeRequest(request)).recentDefenses;
            if (responseItems == undefined) {
                return Promise.reject(new Error(`No recent defenses found. Response: "${JSON.stringify(responseItems)}"`));
            }
            const recentDefenses = responseItems.map((recentDefense) => {
                const teamId = recentDefense.teamId;
                const teamResults = recentDefense.recentDefenses;
                const hydratedDefenses = teamResults.map((defense) => {
                    const team = teams.find((team) => team.id === defense.teamId);
                    if (team) {
                        return new pffkit_js_core_1.RecentDefense(defense.eventId, team, Number(defense.overallSimilarity));
                    }
                    else {
                        throw new Error(`No team found for ${recentDefense.teamId}. Response: "${JSON.stringify(responseItems)}"`);
                    }
                });
                return new pffkit_js_core_1.RecentDefenseList(teamId, hydratedDefenses);
            });
            if (recentDefenses) {
                return [...recentDefenses];
            }
            else {
                return Promise.reject(new Error(`No recent defenses found. Response: "${JSON.stringify(responseItems)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getRecentDefensesForSeason} */
    async getRecentDefensesForSeason(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/recent-defenses`;
        try {
            const teams = await this.getTeamsForSeason(pffkit_js_core_1.Season.twentyTwentyThree);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${pffkit_js_core_1.Season.twentyTwentyThree}`));
            }
            const responseItems = (await this.makeRequest(request)).recentDefenses;
            if (responseItems == undefined) {
                return Promise.reject(new Error(`No recent defenses found. Response: "${JSON.stringify(responseItems)}"`));
            }
            const recentDefenses = responseItems.map((recentDefense) => {
                const teamId = recentDefense.teamId;
                const teamResults = recentDefense.recentDefenses;
                const hydratedDefenses = teamResults.map((defense) => {
                    const team = teams.find((team) => team.id === defense.teamId);
                    if (team) {
                        return new pffkit_js_core_1.RecentDefense(defense.eventId, team, Number(defense.overallSimilarity));
                    }
                    else {
                        throw new Error(`No team found for ${recentDefense.teamId}. Response: "${JSON.stringify(responseItems)}"`);
                    }
                });
                return new pffkit_js_core_1.RecentDefenseList(teamId, hydratedDefenses);
            });
            if (recentDefenses) {
                return [...recentDefenses];
            }
            else {
                return Promise.reject(new Error(`No recent defenses found. Response: "${JSON.stringify(responseItems)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Hot Players API
    /** {@inheritDoc ApiClient.getHotPlayersForSeason} */
    async getHotPlayersForSeason(season, week, sportsbook, markets) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/hot-players`;
        request.queryParameters = new URLSearchParams({
            markets: markets.map((market) => market.toLowerCase()).join(','),
            sportsbook,
        });
        try {
            const data = await this.makeRequest(request);
            const hotPlayers = data?.hotPlayers;
            if (hotPlayers) {
                const hydrated = await Promise.all(hotPlayers.map(async (hotPlayer) => {
                    const profile = await this.getPlayerProfile(hotPlayer.playerId);
                    return (0, hot_players_request_1.createHotPlayer)(hotPlayer, profile);
                }));
                return [...hydrated];
            }
            else {
                return Promise.reject(new Error(`No hot players found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getHotPlayersForEvent} */
    async getHotPlayersForEvent(eventId, sportsbook, markets) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}/hot-players`;
        request.queryParameters = new URLSearchParams({
            markets: markets.map((market) => market.toLowerCase()).join(','),
            sportsbook,
        });
        try {
            const data = await this.makeRequest(request);
            const hotPlayers = data?.hotPlayers;
            if (hotPlayers) {
                const hydrated = await Promise.all(hotPlayers.map(async (hotPlayer) => {
                    const profile = await this.getPlayerProfile(hotPlayer.playerId);
                    return (0, hot_players_request_1.createHotPlayer)(hotPlayer, profile);
                }));
                return [...hydrated];
            }
            else {
                return Promise.reject(new Error(`No hot players found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Historical Performances API
    /** {@inheritDoc ApiClient.getHistoricalPerformancesForPlayer} */
    async getHistoricalPerformancesForPlayer(playerId, market, events) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/players/${playerId}/markets/${market.toLowerCase()}/historical-performances`;
        request.queryParameters = new URLSearchParams({
            eventIds: events.join(','),
        });
        try {
            const data = await this.makeRequest(request);
            const performances = historical_performances_request_1.HistoricalPerformancesResponse.fromJSON(data);
            if (performances) {
                return [...performances];
            }
            else {
                return Promise.reject(new Error(`No historical performances found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Similar Receivers API
    /** {@inheritDoc ApiClient.getReceiversSimilarTo} */
    async getReceiversSimilarTo(receiverId, againstTeamId, market, season, week, events) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/similar-players/${receiverId}/markets/${market.toLowerCase()}/historical-performances`;
        request.queryParameters = new URLSearchParams({
            teamId: againstTeamId.toString(),
            eventIds: events.join(','),
        });
        try {
            const data = await this.makeRequest(request);
            const events = data?.events;
            if (events) {
                return [...events.map((event) => (0, similar_receivers_request_1.createSimilarReceiver)(event))];
            }
            else {
                return Promise.reject(new Error(`No similar players found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Matchup APIs
    /**
     * Get the list of matchups for a given week during an NFL season.
     *
     * @param type - The type of matchup to retrieve.
     * @param season - The season to retrieve matchups for.
     * @param week - The week to retrieve matchups for.
     *
     * @returns A promise that resolves to an array of player matchups.
     * @throws PFFKitError
  
     * @returns
     */
    async getMatchupsForWeek(type, season = this.currentSeason, week) {
        switch (type) {
            case MatchupType.Team:
                return (await this.getTeamMatchupsForWeek(season, week));
            case MatchupType.QuarterBack:
                return (await this.getQuarterbackMatchupsForWeek(season, week));
            case MatchupType.TightEnd:
                return (await this.getTightEndMatchupsForWeek(season, week));
            case MatchupType.RunningBackRushing:
                return (await this.getRunningBackRushingMatchupsForWeek(season, week));
            case MatchupType.RunningBackReceiving:
                return (await this.getRunningBackReceivingMatchupsForWeek(season, week));
            case MatchupType.WideReceiver:
                return (await this.getReceiverMatchupsForWeek(season, week));
        }
        return [];
    }
    /** {@inheritDoc ApiClient.getUnitMatchupsForPlayer} */
    async getUnitMatchupsForPlayer(playerId, market, eventId) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}/markets/${market.toLowerCase()}/players/${playerId}/unit-matchups`;
        try {
            const data = await this.makeRequest(request);
            const matchups = data?.unitMatchups;
            if (matchups) {
                return [...matchups];
            }
            else {
                return Promise.reject(new Error(`No mismatches found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getMatchupsForPlayer} */
    async getMatchupsForPlayer(playerId, eventId) {
        return await this.getTeamMatchupsAgainstPlayer(playerId, eventId);
    }
    /** {@inheritDoc ApiClient.getTeamMatchupsAgainstPlayer} */
    async getTeamMatchupsAgainstPlayer(playerId, eventId) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}/players/${playerId}/team-matchups`;
        try {
            const season = this.currentSeason;
            const response = await this.makeRequest(request);
            const player = await this.getPlayerProfile(playerId);
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            const matchups = Promise.all(response.matchups.map((matchup) => {
                const team = teams.find((team) => team.id === matchup.teamId);
                if (team) {
                    return (0, player_team_matchup_request_1.mapToPlayerVsTeamMatchup)(matchup, player, team, eventId);
                }
                else {
                    throw new Error(`No team found for ${matchup.teamId}. Response: "${JSON.stringify(response)}"`);
                }
            }));
            return Promise.resolve(await matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getPlayerMatchupsForWeek} */
    async getPlayerMatchupsForWeek(season = this.currentSeason, week) {
        return await this.getTeamMatchupsForWeek(season, week);
    }
    /** {@inheritDoc ApiClient.getTeamMatchupsForWeek} */
    async getTeamMatchupsForWeek(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/team-matchups`;
        try {
            const response = await this.makeRequest(request);
            const playerIds = response.matchups.map((matchup) => matchup.playerId);
            const players = await this.getPlayerProfiles(playerIds);
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            if (players.length == 0) {
                return Promise.reject(new Error(`No players found for playerIds ${playerIds.join(', ')}`));
            }
            const matchups = await Promise.all(response.matchups.flatMap((matchup) => {
                return matchup.matchups.map((data) => {
                    const player = players.find((player) => player.id === matchup.playerId);
                    const team = teams.find((team) => team.id === data.teamId);
                    if (team && player) {
                        return (0, player_team_matchup_request_1.mapToPlayerVsTeamMatchup)(data, player, team, matchup.eventId);
                    }
                    else {
                        throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(matchup)}"`);
                    }
                });
            }));
            return Promise.resolve(matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getMatchupsForReceiver} */
    async getMatchupsForReceiver(receiverId, eventId) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}/players/${receiverId}/wr-cb-matchups`;
        try {
            const data = await this.makeRequest(request);
            const playerIds = [...data.matchups.map((matchup) => matchup.playerId), receiverId];
            const players = await this.getPlayerProfiles(playerIds);
            const teamMatchups = await this.getTeamMatchupsAgainstPlayer(receiverId, eventId);
            if (teamMatchups.length > 1) {
                throw new Error(`Expected only one team matchup for receiver ${receiverId} in event ${eventId}`);
            }
            const teamMatchup = teamMatchups[0];
            const hydrated = Promise.all(data.matchups.map((matchup) => {
                const player = players.find((player) => player.id === matchup.playerId);
                if (player) {
                    return (0, wr_cb_request_1.mapToReceiverMatchups)(matchup, player);
                }
                else {
                    throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(data)}"`);
                }
            }));
            const player = players.find((player) => player.id === data.playerId);
            let grade = undefined;
            if (data.facet != null && data.gradeValue != null && data.gradeRank != null && data.gradeRankTotal != null) {
                grade = {
                    rank: data.gradeRank,
                    totalRank: data.gradeRankTotal,
                    grade: data.gradeValue,
                    facet: data.facet,
                };
            }
            const matchups = {
                receiver: player,
                overallScore: data.overallMatchupScore,
                grade: grade,
                defendingTeam: teamMatchup.opponent,
                defendingTeamGrade: teamMatchup.teamGrade,
                matchups: await hydrated,
            };
            return Promise.resolve(matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getReceiverMatchupsForWeek} */
    async getReceiverMatchupsForWeek(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/wr-cb-matchups`;
        try {
            const data = await this.makeRequest(request);
            if (!data.matchups.length) {
                return Promise.resolve([]);
            }
            const receiverIds = data.matchups.map((matchup) => matchup.playerId);
            const cornerIds = data.matchups.map((matchup) => matchup.matchups.map((corner) => corner.playerId));
            const playerIds = [...receiverIds, ...cornerIds.flat()];
            const players = await this.getPlayerProfiles(playerIds);
            const teamMatchups = await this.getTeamMatchupsForWeek(season, week);
            if (teamMatchups.length == 0) {
                return Promise.reject(new Error(`No team matchups found for season ${season} week ${week}`));
            }
            const hydrated = await Promise.all(data.matchups.map((matchup) => {
                const mapped = matchup.matchups.map((data) => {
                    const player = players.find((player) => player.id === data.playerId);
                    if (player) {
                        return (0, wr_cb_request_1.mapToReceiverMatchups)(data, player);
                    }
                    else {
                        throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(data)}"`);
                    }
                });
                const player = players.find((player) => player.id === matchup.playerId);
                const teamMatchup = teamMatchups.find((teamMatchup) => teamMatchup.player.id === player?.id && teamMatchup.teamGrade.facet == pffkit_js_core_1.CoverageGradeFacet.Coverage);
                let grade = undefined;
                if (matchup.facet != null &&
                    matchup.gradeValue != null &&
                    matchup.gradeRank != null &&
                    matchup.gradeRankTotal != null) {
                    grade = {
                        rank: matchup.gradeRank,
                        totalRank: matchup.gradeRankTotal,
                        grade: matchup.gradeValue,
                        facet: matchup.facet,
                    };
                }
                const matchups = {
                    receiver: player,
                    overallScore: matchup.overallMatchupScore,
                    grade: grade,
                    defendingTeam: teamMatchup?.opponent ?? '',
                    defendingTeamGrade: teamMatchup?.teamGrade ?? null,
                    matchups: [...mapped],
                };
                return matchups;
            }));
            return Promise.resolve([...hydrated]);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getTightEndMatchupsForWeek} */
    async getTightEndMatchupsForWeek(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/te-matchups`;
        try {
            const data = await this.makeRequest(request);
            if (!data.matchups.length) {
                return Promise.resolve([]);
            }
            const playerIds = data.matchups.map((matchup) => matchup.playerId);
            const players = await this.getPlayerProfiles(playerIds);
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            if (players.length == 0) {
                return Promise.reject(new Error(`No players found for playerIds ${playerIds.join(', ')}`));
            }
            const matchups = await Promise.all(data.matchups.flatMap((matchup) => {
                const player = players.find((player) => player.id === matchup.playerId);
                const team = teams.find((team) => team.id === matchup.defendingTeamId);
                if (team && player) {
                    return (0, te_matchups_request_1.mapToTightEndMatchup)(matchup, player, team, matchup.eventId);
                }
                else {
                    throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(matchup)}"`);
                }
            }));
            return Promise.resolve(matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getQuarterbackMatchupsForWeek} */
    async getQuarterbackMatchupsForWeek(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/qb-matchups`;
        try {
            const data = await this.makeRequest(request);
            if (!data.matchups.length) {
                return Promise.resolve([]);
            }
            const playerIds = data.matchups.map((matchup) => matchup.playerId);
            const players = await this.getPlayerProfiles(playerIds);
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            if (players.length == 0) {
                return Promise.reject(new Error(`No players found for playerIds ${playerIds.join(', ')}`));
            }
            const matchups = await Promise.all(data.matchups.flatMap((matchup) => {
                const player = players.find((player) => player.id === matchup.playerId);
                const team = teams.find((team) => team.id === matchup.defendingTeamId);
                if (team && player) {
                    return (0, qb_matchups_request_1.mapToQBMatchup)(matchup, player, team, matchup.eventId);
                }
                else {
                    throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(matchup)}"`);
                }
            }));
            return Promise.resolve(matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getRunningBackRushingMatchupsForWeek} */
    async getRunningBackRushingMatchupsForWeek(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/rb-rushing-matchups`;
        try {
            const data = await this.makeRequest(request);
            if (!data.matchups.length) {
                return Promise.resolve([]);
            }
            const playerIds = data.matchups.map((matchup) => matchup.playerId);
            const players = await this.getPlayerProfiles(playerIds);
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            if (players.length == 0) {
                return Promise.reject(new Error(`No players found for playerIds ${playerIds.join(', ')}`));
            }
            const matchups = await Promise.all(data.matchups.flatMap((matchup) => {
                const player = players.find((player) => player.id === matchup.playerId);
                const team = teams.find((team) => team.id === matchup.defendingTeamId);
                if (team && player) {
                    return (0, rb_matchups_request_1.mapToRushingMatchup)(matchup, player, team, matchup.eventId);
                }
                else {
                    throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(matchup)}"`);
                }
            }));
            return Promise.resolve(matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getRunningBackReceivingMatchupsForWeek} */
    async getRunningBackReceivingMatchupsForWeek(season, week) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/rb-receiving-matchups`;
        try {
            const data = await this.makeRequest(request);
            if (!data.matchups.length) {
                return Promise.resolve([]);
            }
            const playerIds = data.matchups.map((matchup) => matchup.playerId);
            const players = await this.getPlayerProfiles(playerIds);
            const teams = await this.getTeamsForSeason(season);
            if (teams.length == 0) {
                return Promise.reject(new Error(`No teams found for season ${season}`));
            }
            if (players.length == 0) {
                return Promise.reject(new Error(`No players found for playerIds ${playerIds.join(', ')}`));
            }
            const matchups = await Promise.all(data.matchups.flatMap((matchup) => {
                const player = players.find((player) => player.id === matchup.playerId);
                const team = teams.find((team) => team.id === matchup.defendingTeamId);
                if (team && player) {
                    return (0, rb_matchups_request_1.mapToReceivingMatchup)(matchup, player, team, matchup.eventId);
                }
                else {
                    throw new Error(`No player found for ${matchup.playerId}. Response: "${JSON.stringify(matchup)}"`);
                }
            }));
            return Promise.resolve(matchups);
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Mismatches API
    /** {@inheritDoc ApiClient.getMismatchesForEvent} */
    async getMismatchesForEvent(eventId, sportsbook, markets) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/events/${eventId}/biggest-mismatches`;
        request.queryParameters = new URLSearchParams({
            markets: markets.map((market) => market.toLowerCase()).join(','),
            sportsbook,
        });
        try {
            const data = await this.makeRequest(request);
            const mismatches = data?.mismatches;
            if (mismatches) {
                const hydrated = await Promise.all(mismatches.map(async (mismatch) => {
                    const profile = await this.getPlayerProfile(mismatch.playerId);
                    return (0, mismatches_request_1.createPlayerMismatch)(mismatch, profile);
                }));
                return Promise.resolve(hydrated);
            }
            else {
                return Promise.reject(new Error(`No mismatches found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /** {@inheritDoc ApiClient.getMismatchesForSeason} */
    async getMismatchesForSeason(season, week, sportsbook, markets) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week}/biggest-mismatches`;
        request.queryParameters = new URLSearchParams({
            markets: markets.map((market) => market.toLowerCase()).join(','),
            sportsbook,
        });
        try {
            const data = await this.makeRequest(request);
            const mismatches = data?.mismatches;
            if (mismatches) {
                const hydrated = await Promise.all(mismatches.map(async (mismatch) => {
                    const profile = await this.getPlayerProfile(mismatch.playerId);
                    return (0, mismatches_request_1.createPlayerMismatch)(mismatch, profile);
                }));
                return Promise.resolve(hydrated);
            }
            else {
                return Promise.reject(new Error(`No mismatches found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Deprecated
    /**
     * Get player props for a given game.
     *
     * @param eventId - The ID of the event to retrieve player props for.
     * @param sportsbook - The sportsbook to use when retrieving betting information for the player props.
     * @param includeDetails - Whether or not to include detailed player prop information.
     * @returns A promise that resolves to an array of player props.
     * @throws PFFKitError
     *
     * @deprecated Use `getPropBets` instead.
     */
    async getPlayerPropsForEvent(eventId, sportsbook, includeDetails) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/player-props/event`;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            eventId: eventId.toString(),
            detailed: includeDetails ? 'true' : 'false',
        });
        try {
            const data = await this.makeRequest(request);
            const props = data?.analysis;
            if (props) {
                return [...props];
            }
            else {
                return Promise.reject(new Error(`No props found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    /**
     * Get player props for a given team.
     *
     * @param teamId - The ID of the team to retrieve player props for.
     * @param season - The season to retrieve player props for.
     * @param week - The week to retrieve player props for.
     * @param sportsbook - The sportsbook to use when retrieving betting information for the player props.
     * @param includeDetails - Whether or not to include detailed player prop information.
     * @returns A promise that resolves to an array of player props.
     * @throws PFFKitError
     *
     * @deprecated Use `getPropBets` instead.
     */
    async getPlayerPropsForTeam(teamId, season, week, sportsbook, includeDetails) {
        const request = {
            ...pffkit_js_core_1.ApiRequestDefaults,
        };
        request.path = `/seasons/${season}/weeks/${week.toString()}/teams/${teamId}/player-props`;
        request.queryParameters = new URLSearchParams({
            sportsbook,
            detailed: includeDetails ? 'true' : 'false',
        });
        try {
            const data = await this.makeRequest(request);
            const props = data?.analysis;
            if (props) {
                return [...props];
            }
            else {
                return Promise.reject(new Error(`No props found. Response: "${JSON.stringify(data)}"`));
            }
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    //#endregion
    //#region Private / Convenience
    async getEvents(eventIds, sportsBook, includeScoringData = true) {
        const events = await Promise.all(eventIds.map(async (eventId) => {
            const event = await this.getEventWithID(eventId, sportsBook, includeScoringData);
            return event;
        }));
        return Promise.resolve(events);
    }
    async makeRequest(request) {
        if (!this.environment.apiKey) {
            return Promise.reject(new Error('API key not set.'));
        }
        const buildRequest = new pffkit_js_core_1.ApiRequestBuilder(request, this.environment);
        const generatedRequest = buildRequest.build();
        const response = await fetch(generatedRequest);
        if (response.ok) {
            const data = (await response.json());
            return Promise.resolve(data);
        }
        else {
            const error = await this.parseError(response);
            return Promise.reject(error);
        }
    }
    async parseError(response) {
        const status = response.status;
        const errorResponse = (await response.json());
        const error = new pffkit_js_core_1.PFFKitError(status, errorMessage(errorResponse));
        return Promise.resolve(error);
    }
}
exports.PFFKit = PFFKit;
//# sourceMappingURL=client.js.map